import axios from 'axios';
import { environment } from '../../environments/environment';

// Helper function to get Google Drive API URL
const getGoogleDriveApiUrl = () => {
  return environment.googleDriveApiUrl || environment.apiUrl;
};

// Helper function to build API URL with proper endpoint
const buildApiUrl = (endpoint: string) => {
  const baseUrl = getGoogleDriveApiUrl();
  return `${baseUrl}/analytics/google-drive${endpoint}`;
};

// Interfaces
export interface GoogleDriveFile {
  id: string;
  name: string;
  mimeType: string;
  size?: string;
  modifiedTime: string;
  parents?: string[];
  webViewLink?: string;
  iconLink?: string;
  thumbnailLink?: string;
  webContentLink?: string;
  previewAvailable?: boolean;
  previewType?: string;
}

export interface GoogleDriveFolder {
  id: string;
  name: string;
  mimeType: string;
  modifiedTime: string;
  children?: GoogleDriveFolder[];
}

export interface GoogleDriveBreadcrumb {
  id: string;
  name: string;
}

export interface GoogleDrivePreviewInfo {
  id: string;
  name: string;
  mimeType: string;
  size: string;
  modifiedTime: string;
  webViewLink: string;
  webContentLink: string;
  iconLink: string;
  thumbnailLink: string;
  previewAvailable: boolean;
  previewType: string;
  previewData: {
    url: string;
    content?: string;
  };
}

export interface GoogleDriveAuthState {
  isAuthenticated: boolean;
  sessionId: string;
  accessToken?: string;
  refreshToken?: string;
}

export interface GoogleDriveServiceCallbacks {
  onAuthStateChange: (state: GoogleDriveAuthState) => void;
  onFilesLoaded: (files: GoogleDriveFile[], totalFiles: number) => void;
  onError: (error: string) => void;
  onSuccess: (message: string) => void;
  onDownloadProgress: (progress: number) => void;
  onLoadingStateChange: (isLoading: boolean) => void;
  onFilePreviewLoaded: (previewInfo: GoogleDrivePreviewInfo, previewUrl: string, previewContent: string) => void;
  onFolderBreadcrumbLoaded: (breadcrumb: GoogleDriveBreadcrumb[]) => void;
  onFolderTreeLoaded: (folderTree: GoogleDriveFolder[]) => void;
  onFileInfoLoaded: (fileInfo: any) => void;
}

export class GoogleDriveService {
  private callbacks: GoogleDriveServiceCallbacks;
  private authWindow: Window | null = null;
  private messageListener: ((event: MessageEvent) => void) | null = null;
  private isDestroyed: boolean = false;

  constructor(callbacks: GoogleDriveServiceCallbacks) {
    this.callbacks = callbacks;
  }

  // Initiate Google Drive authentication
  async initiateAuth(): Promise<{ sessionId: string; authUrl: string }> {
    if (this.isDestroyed) {
      throw new Error('Service has been destroyed');
    }
    
    try {
      const response = await axios.get(buildApiUrl('/auth/url'), {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        }
      });
      
      if (response.data.success && response.data.authUrl) {
        const sessionId = response.data.sessionId;
        localStorage.setItem('googleDriveSessionId', sessionId);
        
        return { sessionId, authUrl: response.data.authUrl };
      } else {
        throw new Error('Failed to get authentication URL');
      }
    } catch (error: any) {
      if (error?.response?.data?.error === 'jwt malformed' || error?.response?.data?.error === 'jwt expired') {
        throw new Error('jwt_expired');
      }
      throw new Error(error?.response?.data?.error || error?.message || 'Failed to initiate Google Drive authentication');
    }
  }

  // Open authentication window
  openAuthWindow(authUrl: string): Window | null {
    const url = new URL(authUrl);
    url.searchParams.set('prompt', 'select_account');
    url.searchParams.set('access_type', 'offline');
    
    const authWindow = window.open(
      url.toString(),
      '_blank',
      'width=600,height=700,scrollbars=yes,resizable=yes,location=yes,status=yes'
    );
    
    if (authWindow) {
      this.authWindow = authWindow;
      this.setupMessageListener();
    }
    
    return authWindow;
  }

  // Setup message listener for OAuth callback
  private setupMessageListener() {
    this.messageListener = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) {
        return;
      }
      
      if (event.data.type === 'GOOGLE_DRIVE_AUTH_SUCCESS') {
        this.handleAuthSuccess();
      } else if (event.data.type === 'GOOGLE_DRIVE_AUTH_ERROR') {
        this.handleAuthError(event.data.error || 'Authentication failed');
      }
    };
    
    window.addEventListener('message', this.messageListener);
  }

  // Handle authentication success
  private handleAuthSuccess() {
    if (this.authWindow) {
      try {
        this.authWindow.close();
      } catch (error) {
        // Ignore close errors
      }
      this.authWindow = null;
    }
    
    const sessionId = localStorage.getItem('googleDriveSessionId');
    if (sessionId) {
      this.callbacks.onAuthStateChange({
        isAuthenticated: true,
        sessionId
      });
      this.callbacks.onSuccess('✅ Google Drive authentication completed!');
    }
    
    this.cleanup();
  }

  // Handle authentication error
  private handleAuthError(error: string) {
    if (this.authWindow) {
      this.authWindow.close();
      this.authWindow = null;
    }
    
    this.callbacks.onError(error);
    this.cleanup();
  }

  // Cleanup resources
  private cleanup() {
    if (this.messageListener) {
      window.removeEventListener('message', this.messageListener);
      this.messageListener = null;
    }
  }

  // Check authentication status
  async checkAuthStatus(sessionId: string): Promise<boolean> {
    if (this.isDestroyed) {
      throw new Error('Service has been destroyed');
    }
    
    try {
      const response = await axios.get(buildApiUrl(`/auth/status?sessionId=${sessionId}`), {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        }
      });

      if (response.data.success) {
        const isAuthenticated = response.data.authenticated || 
                               (response.data.sessionInfo && response.data.sessionInfo.hasTokens);
        return isAuthenticated;
      }
      return false;
    } catch (error: any) {
      if (error?.response?.data?.error === 'Session not authenticated') {
        return false;
      }
      return false;
    }
  }

  // Load Google Drive files (My Drive)
  async loadFiles(sessionId: string, searchQuery?: string, folderId?: string, page: number = 1, pageSize: number = 20): Promise<{ files: GoogleDriveFile[]; totalFiles: number; folderId?: string }> {
    if (this.isDestroyed) {
      throw new Error('Service has been destroyed');
    }
    
    try {
      this.callbacks.onLoadingStateChange(true);
      
      let response;
      
      if (searchQuery && searchQuery.trim()) {
        const searchParams = new URLSearchParams({
          query: searchQuery.trim(),
          sessionId: sessionId,
          pageSize: pageSize.toString()
        });
        
        const searchUrl = buildApiUrl(`/files/search?${searchParams.toString()}`);
        
        response = await axios.get(searchUrl, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          }
        });
      } else {
        const targetFolderId = folderId || 'root';
        const params = new URLSearchParams({
          sessionId: sessionId,
          pageSize: pageSize.toString(),
          page: page.toString()
        });

        const filesUrl = buildApiUrl(`/folders/${targetFolderId}/files?${params.toString()}`);
        
        response = await axios.get(filesUrl, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          }
        });
      }

      if (response.data.success) {
        const result = {
          files: response.data.files || [],
          totalFiles: response.data.totalFiles || 0,
          folderId: response.data.folderId
        };
        
        this.callbacks.onFilesLoaded(result.files, result.totalFiles);
        return result;
      } else {
        throw new Error(response.data.error || 'Failed to load files');
      }
    } catch (error: any) {
      if (error?.response?.data?.error === 'jwt malformed' || error?.response?.data?.error === 'jwt expired') {
        throw new Error('jwt_expired');
      } else if (error?.response?.data?.error === 'Session not authenticated') {
        throw new Error('Session not authenticated');
      } else {
        throw new Error(error?.response?.data?.error || error?.message || 'Failed to load Google Drive files');
      }
    } finally {
      this.callbacks.onLoadingStateChange(false);
    }
  }

  // Load My Drive files
  async loadMyDriveFiles(sessionId: string, pageSize: number = 50): Promise<{ files: GoogleDriveFile[]; totalFiles: number }> {
    if (this.isDestroyed) {
      throw new Error('Service has been destroyed');
    }
    
    try {
      this.callbacks.onLoadingStateChange(true);
      
      const response = await axios.get(buildApiUrl(`/files?sessionId=${sessionId}&pageSize=${pageSize}`), {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        }
      });

      if (response.data.success) {
        const result = {
          files: response.data.files || [],
          totalFiles: response.data.totalFiles || 0
        };
        
        this.callbacks.onFilesLoaded(result.files, result.totalFiles);
        return result;
      } else {
        throw new Error(response.data.error || 'Failed to load My Drive files');
      }
    } catch (error: any) {
      if (error?.response?.data?.error === 'jwt malformed' || error?.response?.data?.error === 'jwt expired') {
        throw new Error('jwt_expired');
      } else if (error?.response?.data?.error === 'Session not authenticated') {
        throw new Error('Session not authenticated');
      } else {
        throw new Error(error?.response?.data?.error || error?.message || 'Failed to load My Drive files');
      }
    } finally {
      this.callbacks.onLoadingStateChange(false);
    }
  }

  // Load Shared files (Shared with me)
  async loadSharedFiles(sessionId: string, pageSize: number = 50): Promise<{ files: GoogleDriveFile[]; totalFiles: number }> {
    if (this.isDestroyed) {
      throw new Error('Service has been destroyed');
    }
    
    try {
      this.callbacks.onLoadingStateChange(true);
      
      const response = await axios.get(buildApiUrl(`/files/shared?sessionId=${sessionId}&pageSize=${pageSize}`), {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        }
      });

      if (response.data.success) {
        const result = {
          files: response.data.files || [],
          totalFiles: response.data.totalFiles || 0
        };
        
        this.callbacks.onFilesLoaded(result.files, result.totalFiles);
        return result;
      } else {
        throw new Error(response.data.error || 'Failed to load shared files');
      }
    } catch (error: any) {
      if (error?.response?.data?.error === 'jwt malformed' || error?.response?.data?.error === 'jwt expired') {
        throw new Error('jwt_expired');
      } else if (error?.response?.data?.error === 'Session not authenticated') {
        throw new Error('Session not authenticated');
      } else {
        throw new Error(error?.response?.data?.error || error?.message || 'Failed to load shared files');
      }
    } finally {
      this.callbacks.onLoadingStateChange(false);
    }
  }

  // Load Team Drives (Shared drives)
  async loadTeamDrives(sessionId: string, pageSize: number = 50): Promise<{ drives: GoogleDriveFile[]; totalDrives: number }> {
    if (this.isDestroyed) {
      throw new Error('Service has been destroyed');
    }
    
    try {
      this.callbacks.onLoadingStateChange(true);
      
      const response = await axios.get(buildApiUrl(`/drives?sessionId=${sessionId}&pageSize=${pageSize}`), {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        }
      });

      if (response.data.success) {
        const result = {
          drives: response.data.drives || [],
          totalDrives: response.data.totalDrives || 0
        };
        
        this.callbacks.onFilesLoaded(result.drives, result.totalDrives);
        return result;
      } else {
        throw new Error(response.data.error || 'Failed to load team drives');
      }
    } catch (error: any) {
      if (error?.response?.data?.error === 'jwt malformed' || error?.response?.data?.error === 'jwt expired') {
        throw new Error('jwt_expired');
      } else if (error?.response?.data?.error === 'Session not authenticated') {
        throw new Error('Session not authenticated');
      } else {
        throw new Error(error?.response?.data?.error || error?.message || 'Failed to load team drives');
      }
    } finally {
      this.callbacks.onLoadingStateChange(false);
    }
  }

  // Load files from specific Team Drive
  async loadTeamDriveFiles(sessionId: string, teamDriveId: string, pageSize: number = 50): Promise<{ files: GoogleDriveFile[]; totalFiles: number }> {
    if (this.isDestroyed) {
      throw new Error('Service has been destroyed');
    }
    
    try {
      this.callbacks.onLoadingStateChange(true);
      
      const response = await axios.get(buildApiUrl(`/drives/${teamDriveId}/files?sessionId=${sessionId}&pageSize=${pageSize}`), {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        }
      });

      if (response.data.success) {
        const result = {
          files: response.data.files || [],
          totalFiles: response.data.totalFiles || 0
        };
        
        this.callbacks.onFilesLoaded(result.files, result.totalFiles);
        return result;
      } else {
        throw new Error(response.data.error || 'Failed to load team drive files');
      }
    } catch (error: any) {
      if (error?.response?.data?.error === 'jwt malformed' || error?.response?.data?.error === 'jwt expired') {
        throw new Error('jwt_expired');
      } else if (error?.response?.data?.error === 'Session not authenticated') {
        throw new Error('Session not authenticated');
      } else {
        throw new Error(error?.response?.data?.error || error?.message || 'Failed to load team drive files');
      }
    } finally {
      this.callbacks.onLoadingStateChange(false);
    }
  }

  // Search all files across all tabs
  async searchAllFiles(sessionId: string, query: string, pageSize: number = 50): Promise<{ files: GoogleDriveFile[]; totalFiles: number }> {
    if (this.isDestroyed) {
      throw new Error('Service has been destroyed');
    }
    
    try {
      this.callbacks.onLoadingStateChange(true);
      
      const response = await axios.get(buildApiUrl(`/files/search-all?query=${encodeURIComponent(query)}&sessionId=${sessionId}&pageSize=${pageSize}`), {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        }
      });

      if (response.data.success) {
        const result = {
          files: response.data.files || [],
          totalFiles: response.data.totalFiles || 0
        };
        
        this.callbacks.onFilesLoaded(result.files, result.totalFiles);
        return result;
      } else {
        throw new Error(response.data.error || 'Failed to search files');
      }
    } catch (error: any) {
      if (error?.response?.data?.error === 'jwt malformed' || error?.response?.data?.error === 'jwt expired') {
        throw new Error('jwt_expired');
      } else if (error?.response?.data?.error === 'Session not authenticated') {
        throw new Error('Session not authenticated');
      } else {
        throw new Error(error?.response?.data?.error || error?.message || 'Failed to search files');
      }
    } finally {
      this.callbacks.onLoadingStateChange(false);
    }
  }

  // Load all navigation data at once
  async loadAllNavigation(sessionId: string, pageSize: number = 20): Promise<{ myDrive: any; shared: any; teamDrives: any }> {
    if (this.isDestroyed) {
      throw new Error('Service has been destroyed');
    }
    
    try {
      this.callbacks.onLoadingStateChange(true);
      
      const response = await axios.get(buildApiUrl(`/picker/navigation?sessionId=${sessionId}&pageSize=${pageSize}`), {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        }
      });

      if (response.data.success) {
        const navigation = response.data.navigation;
        this.callbacks.onFilesLoaded(navigation.myDrive.files, navigation.myDrive.totalFiles);
        return navigation;
      } else {
        throw new Error(response.data.error || 'Failed to load navigation data');
      }
    } catch (error: any) {
      if (error?.response?.data?.error === 'jwt malformed' || error?.response?.data?.error === 'jwt expired') {
        throw new Error('jwt_expired');
      } else if (error?.response?.data?.error === 'Session not authenticated') {
        throw new Error('Session not authenticated');
      } else {
        throw new Error(error?.response?.data?.error || error?.message || 'Failed to load navigation data');
      }
    } finally {
      this.callbacks.onLoadingStateChange(false);
    }
  }

  // Get folder breadcrumb
  async getFolderBreadcrumb(sessionId: string, folderId: string): Promise<GoogleDriveBreadcrumb[]> {
    try {
      const response = await axios.get(
        buildApiUrl(`/folders/${folderId}/breadcrumb?sessionId=${sessionId}`),
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          }
        }
      );

      if (response.data.success) {
        const breadcrumb = response.data.breadcrumb;
        
        // Remove duplicates based on both ID and name
        const uniqueBreadcrumb = breadcrumb.filter((item: GoogleDriveBreadcrumb, index: number, self: GoogleDriveBreadcrumb[]) => {
          if (item.name === "My Drive") {
            return item.id === "root" || index === self.findIndex((t: GoogleDriveBreadcrumb) => t.name === "My Drive");
          }
          return index === self.findIndex((t: GoogleDriveBreadcrumb) => t.id === item.id);
        });
        
        this.callbacks.onFolderBreadcrumbLoaded(uniqueBreadcrumb);
        return uniqueBreadcrumb;
      } else {
        throw new Error(response.data.error || 'Failed to get breadcrumb');
      }
    } catch (error: any) {
      if (error?.response?.data?.error === 'jwt malformed' || error?.response?.data?.error === 'jwt expired') {
        throw new Error('jwt_expired');
      } else if (error?.response?.data?.error === 'Session not authenticated') {
        throw new Error('Session not authenticated');
      }
      throw new Error(error?.response?.data?.error || error?.message || 'Failed to get folder breadcrumb');
    }
  }

  // Get file preview information
  async getFilePreviewInfo(sessionId: string, fileId: string): Promise<{ previewInfo: GoogleDrivePreviewInfo; previewUrl: string; previewContent: string }> {
    try {
      this.callbacks.onLoadingStateChange(true);
      
      const response = await axios.get(
        `${getGoogleDriveApiUrl()}/analytics/google-drive/files/${fileId}/preview-info?sessionId=${sessionId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          }
        }
      );

      if (response.data.success) {
        const previewInfo = response.data.preview;
        let previewUrl = '';
        let previewContent = '';
        
        if (previewInfo.previewAvailable) {
          switch (previewInfo.previewType) {
            case 'image':
            case 'video':
            case 'audio':
            case 'pdf':
              previewUrl = `${getGoogleDriveApiUrl()}${previewInfo.previewData.url}`;
              break;
            case 'text':
              const textResponse = await axios.get(
                `${getGoogleDriveApiUrl()}/analytics/google-drive/files/${fileId}/text-content?sessionId=${sessionId}`,
                {
                  headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/json',
                  }
                }
              );
              if (textResponse.data.success) {
                previewContent = textResponse.data.content;
              }
              break;
          }
        }
        
        this.callbacks.onFilePreviewLoaded(previewInfo, previewUrl, previewContent);
        return { previewInfo, previewUrl, previewContent };
      } else {
        throw new Error('Failed to get file preview information');
      }
    } catch (error: any) {
      if (error?.response?.data?.error === 'jwt malformed' || error?.response?.data?.error === 'jwt expired') {
        throw new Error('jwt_expired');
      }
      throw new Error(error?.response?.data?.error || error?.message || 'Failed to get file preview information');
    } finally {
      this.callbacks.onLoadingStateChange(false);
    }
  }

  // Load folder tree structure
  async loadFolderTree(sessionId: string, rootFolderId: string = 'root', maxDepth: number = 3): Promise<GoogleDriveFolder[]> {
    try {
      this.callbacks.onLoadingStateChange(true);
      
      const response = await axios.get(
        `${getGoogleDriveApiUrl()}/analytics/google-drive/folders/tree?sessionId=${sessionId}&rootFolderId=${rootFolderId}&maxDepth=${maxDepth}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          }
        }
      );

      if (response.data.success) {
        this.callbacks.onFolderTreeLoaded(response.data.folderTree);
        return response.data.folderTree;
      } else {
        throw new Error('Failed to load folder tree');
      }
    } catch (error: any) {
      if (error?.response?.data?.error === 'jwt malformed' || error?.response?.data?.error === 'jwt expired') {
        throw new Error('jwt_expired');
      }
      throw new Error(error?.response?.data?.error || error?.message || 'Failed to load folder tree');
    } finally {
      this.callbacks.onLoadingStateChange(false);
    }
  }

  // Get file info
  async getFileInfo(sessionId: string, fileId: string): Promise<any> {
    try {
      this.callbacks.onLoadingStateChange(true);
      
      const response = await axios.get(
        `${getGoogleDriveApiUrl()}/analytics/google-drive/files/${fileId}/info?sessionId=${sessionId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          }
        }
      );

      if (response.data.success) {
        this.callbacks.onFileInfoLoaded(response.data.fileInfo);
        return response.data.fileInfo;
      } else {
        throw new Error('Failed to get file information');
      }
    } catch (error: any) {
      if (error?.response?.data?.error === 'jwt malformed' || error?.response?.data?.error === 'jwt expired') {
        throw new Error('jwt_expired');
      }
      throw new Error(error?.response?.data?.error || error?.message || 'Failed to get file information');
    } finally {
      this.callbacks.onLoadingStateChange(false);
    }
  }

  // Get download URL for a file
  async getDownloadUrl(sessionId: string, fileId: string): Promise<string> {
    try {
      const response = await axios.get(
        `${getGoogleDriveApiUrl()}/analytics/google-drive/files/${fileId}/download-url?sessionId=${sessionId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          }
        }
      );

      if (response.data.success && response.data.file.downloadUrl) {
        return response.data.file.downloadUrl;
      } else {
        throw new Error('Failed to get download URL');
      }
    } catch (error: any) {
      if (error?.response?.data?.error === 'jwt malformed' || error?.response?.data?.error === 'jwt expired') {
        throw new Error('jwt_expired');
      }
      throw new Error(error?.response?.data?.error || error?.message || 'Failed to get download URL');
    }
  }

  // Download file
  async downloadFile(downloadUrl: string, fileName: string, onProgress?: (progress: number) => void): Promise<File> {
    try {
      const response = await axios.get(
        `${environment.apiUrl}${downloadUrl}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
          responseType: 'blob',
          onDownloadProgress: (progressEvent) => {
            if (progressEvent.total && onProgress) {
              const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              onProgress(progress);
            }
          }
        }
      );

      if (response.data) {
        const blob = new Blob([response.data]);
        const finalFileName = fileName;
        return new File([blob], finalFileName, { type: 'application/octet-stream' });
      
          
      } else {
        throw new Error('No data received');
      }
    } catch (error: any) {
      if (error?.response?.data?.error === 'jwt malformed' || error?.response?.data?.error === 'jwt expired') {
        throw new Error('jwt_expired');
      }
      throw new Error(error?.response?.data?.error || error?.message || 'Failed to download file');
    }
  }

  // Download multiple files
  async downloadMultipleFiles(sessionId: string, files: GoogleDriveFile[], onProgress?: (progress: number) => void): Promise<File[]> {
    try {
      const fileIds = files.map(file => file.id);
      
      const infoResponse = await axios.post(
        `${getGoogleDriveApiUrl()}/analytics/google-drive/files/download-multiple-info`,
        {
          fileIds,
          sessionId
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          }
        }
      );

      if (infoResponse.data.success && infoResponse.data.results) {
        const downloadedFiles: File[] = [];
        
        for (let i = 0; i < infoResponse.data.results.length; i++) {
          const fileInfo = infoResponse.data.results[i];
          if (fileInfo.success) {
            try {
              const downloadUrl = await this.getDownloadUrl(sessionId, fileInfo.id);
              const downloadedFile = await this.downloadFile(downloadUrl, fileInfo.name, onProgress);
              downloadedFiles.push(downloadedFile);
            } catch (error) {
              console.error(`Error downloading file ${fileInfo.name}:`, error);
            }
          }
        }
        
        return downloadedFiles;
      } else {
        throw new Error('Failed to get download info for multiple files');
      }
    } catch (error: any) {
      if (error?.response?.data?.error === 'jwt malformed' || error?.response?.data?.error === 'jwt expired') {
        throw new Error('jwt_expired');
      }
      throw new Error(error?.response?.data?.error || error?.message || 'Failed to download multiple files');
    }
  }

  // Clear authentication
  async clearAuth(sessionId: string): Promise<void> {
    try {
      await axios.post(buildApiUrl('/revoke'), {
        sessionId
      }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        }
      });
    } catch (error) {
      // Ignore revocation errors
    }
    
    localStorage.removeItem('googleDriveSessionId');
  }

  // Check download status
  async checkDownloadStatus(sessionId: string, fileId: string): Promise<any> {
    try {
      const response = await axios.get(
        `${getGoogleDriveApiUrl()}/analytics/google-drive/files/${fileId}/download-status?sessionId=${sessionId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          }
        }
      );

      if (response.data.success) {
        return response.data.status;
      }
      return null;
    } catch (error) {
      console.error('Error checking download status:', error);
      return null;
    }
  }

  // Utility functions
  formatFileSize(bytes: string): string {
    const size = parseInt(bytes);
    if (size === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(size) / Math.log(k));
    return parseFloat((size / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  getFileIcon(mimeType: string): string {
    if (mimeType.includes('spreadsheet') || mimeType.includes('sheet')) {
      return '📊';
    } else if (mimeType.includes('document')) {
      return '📄';
    } else if (mimeType.includes('csv')) {
      return '📋';
    } else if (mimeType.includes('excel')) {
      return '📈';
    } else if (mimeType.includes('image')) {
      return '🖼️';
    } else if (mimeType.includes('video')) {
      return '🎥';
    } else if (mimeType.includes('audio')) {
      return '🎵';
    } else if (mimeType.includes('pdf')) {
      return '📕';
    } else {
      return '📁';
    }
  }

  // Convert Google Drive URL
  convertGoogleDriveUrl(url: string): string {
    if (url.includes('/view') || url.includes('/edit')) {
      const fileId = url.match(/\/d\/([a-zA-Z0-9-_]+)/)?.[1];
      if (fileId) {
        return `https://drive.google.com/uc?export=download&id=${fileId}`;
      }
    }
    return url;
  }

  // Get file name from Google URL
  getFileNameFromGoogleUrl(url: string): string {
    const fileName = url.match(/[^/]+$/)?.[0] || 'google_drive_file';
    return decodeURIComponent(fileName);
  }

  // Check if URL is Google Drive
  isGoogleDriveUrl(url: string): boolean {
    return url.includes('drive.google.com') || url.includes('docs.google.com') || url.includes('sheets.google.com');
  }

  // Cleanup on component unmount
  destroy() {
    this.isDestroyed = true;
    this.cleanup();
    if (this.authWindow && !this.authWindow.closed) {
      this.authWindow.close();
    }
  }
}

export default GoogleDriveService;
